
import Vue from 'vue'
import UserSettings from '@/components/user/Settings.vue'
import { UserV2 } from '@/model/User'
import { Multimedia } from '@/model/Multimedia'
import { Project } from '@/model/Project'
import moment from 'moment'
import FormData from 'form-data'
import Confirm from '@/components/Confirm.vue'
import ProfileCompleteness from '@/components/ProfileCompleteness.vue'
import UserContests from '@/components/contest/UserContests.vue'
import ServerPaginationList from '@/components/ServerPaginationList.vue'
import ArticleCardItem from '@/components/article/CardItem.vue'
import ProjectCardItem from '@/components/project/ItemCard.vue'
import RequestAccountDeleteDialog from '@/components/user/RequestAccountDeleteDialog.vue'
import { AxiosError } from 'axios'

export default Vue.extend({
  name: 'Profile',
  components: {
    UserSettings,
    Confirm,
    ProfileCompleteness,
    UserContests,
    ServerPaginationList,
    ArticleCardItem,
    ProjectCardItem,
    RequestAccountDeleteDialog,
  },
  mounted() {
    this.fetchData()
  },
  computed: {
    isProfileOwner: function () {
      if (!this.$store.state.authentication.signedIn) {
        return false
      }
      return (
        this.$route.params.nickname ===
        this.$store.state.authentication.user.nickname
      )
    },
  },
  data: () => ({
    user: {
      id: '',
    } as UserV2,
    userForm: {
      id: '',
    } as UserV2,
    userAvatar: [],
    avatarLoading: false,
    removeAvatarLoading: false,
    saveChangesLoading: false,
    requestAccountDeleteDialog: false,
    completenessSteps: [] as string[],
    tab: '',
    accountNeedParentConsentMessage: false,
  }),
  methods: {
    fetchData: function () {
      this.$api
        .get('/users/nickname/' + this.$route.params.nickname)
        .then((response) => {
          this.user = response.data
          this.userForm = Object.assign({}, this.user)
          this.completenessSteps = this.getCompletenessSteps()
        })
        .catch((error: any) => {
          if (error.response) {
            if (error.response.status === 469) {
              this.accountNeedParentConsentMessage = true
              return
            }
          }
          this.$router.push('/404')
        })
    },
    getCompletenessSteps(): string[] {
      const result: string[] = []
      if (!this.user.avatar) {
        result.push('Add profile avatar')
      }
      if (
        this.user?.description == null ||
        this.user?.description.length === 0
      ) {
        result.push('Fill informations About you')
      }
      if (
        this.user.facebookUrl.length === 0 &&
        this.user.instagramUrl.length === 0 &&
        this.user.twitterUrl.length === 0
      ) {
        result.push('Share social media links')
      }
      return result
    },
    removeAvatar: function () {
      this.removeAvatarLoading = true
      this.user.avatar = null
      this.userForm.avatar = null

      const dataToApi = Object.assign({}, this.user)
      dataToApi.avatar = null

      this.$api
        .put('/me', dataToApi)
        .then((response) => {
          this.user = response.data
          this.$store.dispatch('authentication/changeAvatar', { url: null })
          this.$store.dispatch('alert/success', 'Avatar has been removed!')
          this.completenessSteps = this.getCompletenessSteps()
        })
        .catch((error) => this.$store.dispatch('alert/error', error.message))
        .finally(() => (this.removeAvatarLoading = false))
    },
    uploadAvatar: function (value: File) {
      if (!value) {
        return
      }
      if (value.size > 5000000) {
        this.userAvatar = []
        this.$store.dispatch('alert/error', 'Maximum image size is 5MB.')
        return
      }
      this.avatarLoading = true
      const userId = this.$store.state.authentication.user
        ? this.$store.state.authentication.user.id
        : 'x-unknown'
      const formData = new FormData()
      formData.append('file', value, value.name)
      formData.append('name', value.name)
      formData.append('description', value.name)
      formData.append('alt', value.name)
      formData.append('userId', userId)
      this.$api
        .post('/assets', formData)
        .then((response) => {
          this.userAvatar = []
          this.user.avatar = response.data
          this.userForm.avatar = response.data
          const dataToApi = Object.assign({}, this.user)
          dataToApi.avatar = (this.user.avatar as Multimedia)?.id ?? null

          this.$api.put('/me', dataToApi).then((response) => {
            this.user = response.data
            this.$store.dispatch('authentication/changeAvatar', {
              avatar: this.user.avatar as Multimedia,
            })
            this.$store.dispatch('alert/success', 'Avatar has changed!')
            this.completenessSteps = this.getCompletenessSteps()
          })
        })
        .catch((error) => this.$store.dispatch('alert/error', error.message))
        .finally(() => (this.avatarLoading = false))
    },
    saveChanges: function () {
      this.saveChangesLoading = true

      if (this.userForm.avatar) {
        this.userForm.avatar = (this.userForm.avatar as Multimedia).id
      }

      if (!this.userForm.position) {
        this.userForm.position = ''
      }

      this.$api
        .put('/me', this.userForm)
        .then((response) => {
          this.user = response.data
          this.userForm = Object.assign({}, response.data)
          this.$store.dispatch('authentication/changeName', {
            firstname: this.user.firstName,
            lastname: this.user.lastName,
          })
          this.$store.dispatch('authentication/changeNickname', {
            nickname: this.user.nickname,
          })
          if (this.user.nickname !== this.$route.params.nickname) {
            this.$router.push('/' + this.user.nickname)
          }
          this.$store.dispatch('alert/success', 'Data successfully saved!')
          this.completenessSteps = this.getCompletenessSteps()
        })
        .catch((reason: AxiosError) => {
          if(reason?.response?.status === 409){
            this.$store.dispatch('alert/error', reason?.response?.data?.msg as string)
          } else{
            this.$store.dispatch('alert/error', 'Something gone wrong.')
          }
        })
        .finally(() => (this.saveChangesLoading = false))
    },
    ensureSafeUrl(url: string) {
      if (/^https?:\/\//i.test(url)) {
        return true
      }
      return false
    },
    deleteProject: function (item: Project) {
      this.$api
        .delete('/projects/' + item.id)
        .then(() => {
          this.$store.dispatch('alert/success', 'Project has been deleted.')
          ;(this.$refs.usersProjectsRef as any).removeItem(item)
        })
        .catch(() => {
          this.$store.dispatch('alert/error', 'Problems with project delete.')
        })
    },
    removeMyAccount: function () {
      this.requestAccountDeleteDialog = true
    },
  },
  watch: {
    $route: function (to, from) {
      this.fetchData()
    },
    'userForm.birthdayAt': function () {
      this.userForm.birthdayAt = moment(this.userForm.birthdayAt).format(
        'YYYY-MM-DD'
      )
    },
  },
})
